import React from 'react';
import PageSection from '../../components/PageSection';
import { useAPI } from '../../api';
import { useNavigate, useParams } from 'react-router-dom';
import PropEditor, { secretProp } from '../../components/PropEditor';
import RawJsonDisplay from '../../components/RawJsonDisplay';

import Localizer from '../../localizer';
import { Firewall, FirewallType } from 'types';
import { OrgContextLink, useOrgContext } from 'components/OrgContextProvider';

interface FirewallContainerProps {
	onChanged: () => void;
}

const FirewallContainer = ({ onChanged }: FirewallContainerProps) => {
	const api = useAPI();
	const navigate = useNavigate();

	const ctx = useOrgContext();

	const { firewallID } = useParams();

	const [firewallTypes, setFirewallTypes] = React.useState<FirewallType[]>([]);
	const [firewall, setFirewall] = React.useState<Firewall>({
		type: 'fortigate',
		name: '',
		address: '',
		settings: {},
		secrets: {}
	});

	React.useEffect(() => {
		if (firewallID !== 'new') {
			api.get<Firewall>(`${ctx?.apiBaseTenant}/firewalls/${firewallID}`)
			.then(o => setFirewall(o));
		}

		api.get<FirewallType[]>(`${ctx?.apiBaseTenant}/firewalls/types`)
		.then(json => {
			setFirewallTypes(json);
		});

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	function testFirewall() {
		api.post(`${ctx?.apiBaseTenant}/firewalls/test`, {
			id: firewallID !== 'new' ? firewallID : undefined,
			name: firewall.name,
			type: firewall.type,
			address: firewall.address,
			settings: firewall.settings,
			secrets: firewall.secrets
		})
		.then(() => alert('Test successful'));
	};

	function saveFirewall() {
		const firewallData = {
			id: firewall.id,
			name: firewall.name,
			type: firewall.type,
			address: firewall.address,
			settings: firewall.settings,
			secrets: firewall.secrets
		};

		if (firewallID === 'new') {
			api.post(`${ctx?.apiBaseTenant}/firewalls`, firewallData)
			.then(onChanged)
			.then(() => navigate(`${ctx?.contextPath}/settings/firewalls`));
		} else {
			api.put(`${ctx?.apiBaseTenant}/firewalls/${firewallID}`, firewallData)
			.then(onChanged)
			.then(() => navigate(`${ctx?.contextPath}/settings/firewalls`));
		}
	}

	return !ctx?.tenantID ? (
		<p>Select a tenant from the selector above.</p>
	) : (
		<div>
			<PageSection>
				<OrgContextLink to={`/settings/firewalls`}>&#x25c0; Back to Firewalls</OrgContextLink>
			</PageSection>

			<PageSection title={firewallID === 'new' ? 'New Firewall' : `Firewall: ${firewall.name}`}>
				<PropEditor<Firewall>
					object={firewall}
					onChange={setFirewall}
					props={[
						{ key: 'id', label: 'ID', disabled: firewallID !== 'new' },
						{ key: 'type', label: 'Type', type: 'select', options: firewallTypes.map(fw => ({ label: `${Localizer.getLocalizedString(fw.name)} (${Localizer.getLocalizedString(fw.vendor)})`, value: fw.id })) },
						{ key: 'name', label: 'Name' },
						{ key: 'address', label: 'Address' },
						secretProp({ context: 'secrets', key: 'accessToken', label: 'Access Token', type: 'password' }),
					]}
				/>

				<button onClick={testFirewall}>Test Firewall</button>
				<button onClick={saveFirewall}>Save Firewall</button>
			</PageSection>

			<PageSection title="Raw Data">
				<RawJsonDisplay data={firewall} />
			</PageSection>

		</div>
	);
};

export default FirewallContainer;